import * as React from "react";
import * as v from "valibot";
import { isEqual } from "lodash";
import { useStateContext } from "../state";
import { useValibotForm } from "@/utils/validation";
import { FormattedMessage, IntlShape, useIntl } from "react-intl";
import {
  Form,
  FormField,
  FormControl,
  FormLabel,
  FormItem,
  FormMessage,
} from "@/kit/ui/form";
import { Input } from "@/kit/ui/input";

const sampleOptionsSchemaFactory = (intl: IntlShape) =>
  v.object({
    sample_size: v.pipe(
      v.string(),
      v.trim(),
      v.check(
        (value) => {
          if (value === "") {
            return true;
          }
          try {
            parseInt(value, 10);
            return true;
          } catch {
            return false;
          }
        },
        intl.formatMessage({
          defaultMessage: "Sample size must be a valid number.",
        }),
      ),
      v.transform((value) => (value === "" ? undefined : parseInt(value, 10))),
    ),
  });

export const SampleOptions = () => {
  const intl = useIntl();
  const { inferSchemaOptions, setInferSchemaOptions } = useStateContext();
  const sampleOptionsSchema = React.useMemo(
    () => sampleOptionsSchemaFactory(intl),
    [intl],
  );
  const form = useValibotForm(sampleOptionsSchema, {
    defaultValues: {
      sample_size: inferSchemaOptions?.sample_size?.toString() || "",
    },
    reValidateMode: "onChange",
  });
  const value = form.watch();
  React.useEffect(() => {
    const { success, output } = v.safeParse(sampleOptionsSchema, value);
    if (success) {
      const newSchemaOptions = {
        ...inferSchemaOptions,
        ...output,
      };
      if (!isEqual(newSchemaOptions, inferSchemaOptions)) {
        setInferSchemaOptions(newSchemaOptions);
      }
    }
  }, [value, sampleOptionsSchema, inferSchemaOptions, setInferSchemaOptions]);
  return (
    <Form form={form}>
      <FormField
        control={form.control}
        name="sample_size"
        render={({ field }) => (
          <FormItem>
            <FormLabel>
              <FormattedMessage defaultMessage="Sample Size" />
            </FormLabel>
            <FormControl>
              <Input {...field} type="number" />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />
    </Form>
  );
};
